import { type NoteGenerationAnalyticsSource } from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import {
  finishTracking,
  type GenerateTrigger,
  recordSessionFlowIntent,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';
import { UserFriendlyNoteFailedReason } from '@heidi/schema/ml-scribe';
import {
  NoteFailedClientErrorCode,
  NoteFailedSourceErrorCode,
  NoteGenerationErrorCode,
} from '@heidi/session/contracts';

// `note_generate` client metrics: every terminal generation closes its attempt
// with a bounded `error_type`, and only an explicit user action emits intent.

export type NoteGenerateTrigger = GenerateTrigger;

export type NoteGenerateErrorName =
  | 'NoteGenerateQuotaExceeded'
  | 'NoteGeneratePermissionDenied'
  | 'NoteGenerateTemplateUnavailable'
  | 'NoteGenerateTranscriptTooShort'
  | 'NoteGenerateTranscriptNotAvailable'
  | 'NoteGenerateTranscriptDeleted'
  | 'NoteGenerateSessionNotTranscribed'
  | 'NoteGenerateTranscriptionFailed'
  | 'NoteGenerateServerError'
  | 'NoteGenerateRateLimited'
  | 'NoteGenerateRejected'
  | 'NoteGenerateTimeout'
  | 'NoteGenerateNetworkUnavailable'
  | 'NoteGenerateAllCandidatesFailed'
  | 'NoteGenerateInterrupted'
  | 'NoteGenerateCancelled'
  | 'NoteGenerateUnclassified';

/**
 * The `note_generate` failure vocabulary, registered with the rest of the
 * session-flow names. `NoteGenerateUnclassified` is absent on purpose, so it
 * lands in `_OTHER`.
 */
export const NOTE_GENERATE_FAILURE_TYPES: Readonly<
  Record<Exclude<NoteGenerateErrorName, 'NoteGenerateUnclassified'>, SessionFlowFailure>
> = {
  NoteGenerateQuotaExceeded: { outcome: 'blocked', errorType: 'quota_exceeded' },
  NoteGeneratePermissionDenied: { outcome: 'blocked', errorType: 'permission_denied' },
  NoteGenerateTemplateUnavailable: {
    outcome: 'failed',
    errorType: 'template_unavailable',
  },
  // A transcript too short or absent is a precondition, not a generation fault.
  NoteGenerateTranscriptTooShort: {
    outcome: 'blocked',
    errorType: 'transcript_too_short',
  },
  NoteGenerateTranscriptNotAvailable: {
    outcome: 'blocked',
    errorType: 'transcript_not_available',
  },
  NoteGenerateTranscriptDeleted: { outcome: 'blocked', errorType: 'transcript_deleted' },
  NoteGenerateSessionNotTranscribed: {
    outcome: 'blocked',
    errorType: 'session_not_transcribed',
  },
  NoteGenerateTranscriptionFailed: {
    outcome: 'failed',
    errorType: 'transcription_failed',
  },
  NoteGenerateServerError: { outcome: 'failed', errorType: 'server_error' },
  NoteGenerateRateLimited: { outcome: 'failed', errorType: 'rate_limited' },
  NoteGenerateRejected: { outcome: 'failed', errorType: 'request_rejected' },
  NoteGenerateTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  NoteGenerateNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  NoteGenerateAllCandidatesFailed: {
    outcome: 'failed',
    errorType: 'all_candidates_failed',
  },
  // A 401: auth ended the generation, not the generation itself.
  NoteGenerateInterrupted: { outcome: 'cancelled' },
  NoteGenerateCancelled: { outcome: 'cancelled' },
};

const NOTE_GENERATE_ERROR_NAMES: ReadonlySet<string> = new Set<NoteGenerateErrorName>([
  ...(Object.keys(NOTE_GENERATE_FAILURE_TYPES) as NoteGenerateErrorName[]),
  'NoteGenerateUnclassified',
]);

/** True for an error already classified into the `note_generate` vocabulary. */
export const isNoteGenerateError = (error: unknown): error is Error =>
  error instanceof Error && NOTE_GENERATE_ERROR_NAMES.has(error.name);

const namedError = (name: NoteGenerateErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

const NAME_BY_CODE: Readonly<Record<string, NoteGenerateErrorName>> = {
  [NoteGenerationErrorCode.UnlimitedQuotaUntilNotSet]: 'NoteGenerateQuotaExceeded',
  [NoteGenerationErrorCode.AskAiQuotaExceed]: 'NoteGenerateQuotaExceeded',
  [NoteGenerationErrorCode.TemplateQuotaExceed]: 'NoteGenerateQuotaExceeded',
  [NoteFailedSourceErrorCode.QuotaExceeded]: 'NoteGenerateQuotaExceeded',
  [NoteGenerationErrorCode.TeamTemplateAccessDenied]: 'NoteGeneratePermissionDenied',
  [NoteGenerationErrorCode.GenerationTemplateAccessDenied]:
    'NoteGeneratePermissionDenied',
  [NoteGenerationErrorCode.TemplateDeleted]: 'NoteGenerateTemplateUnavailable',
  [NoteGenerationErrorCode.TemplateNotFound]: 'NoteGenerateTemplateUnavailable',
  [UserFriendlyNoteFailedReason.transcript_too_short]: 'NoteGenerateTranscriptTooShort',
  [UserFriendlyNoteFailedReason.transcript_not_available]:
    'NoteGenerateTranscriptNotAvailable',
  [NoteFailedSourceErrorCode.TranscriptDeleted]: 'NoteGenerateTranscriptDeleted',
  [NoteFailedSourceErrorCode.SessionNotTranscribed]: 'NoteGenerateSessionNotTranscribed',
  [UserFriendlyNoteFailedReason.transcript_available_but_note_generation_failed]:
    'NoteGenerateServerError',
  [UserFriendlyNoteFailedReason.llm_errored_during_streaming]: 'NoteGenerateServerError',
  [UserFriendlyNoteFailedReason.unexpected_error]: 'NoteGenerateServerError',
  [NoteFailedClientErrorCode.EmptyNoteStream]: 'NoteGenerateServerError',
  // A failed fetch surfaces as a TypeError.
  [NoteFailedClientErrorCode.TypeError]: 'NoteGenerateNetworkUnavailable',
  [NoteFailedClientErrorCode.NetworkError]: 'NoteGenerateNetworkUnavailable',
  // An abort not already closed by the abort handler is still a cancellation.
  [NoteFailedClientErrorCode.AbortError]: 'NoteGenerateCancelled',
};

const nameForHttpStatus = (status: number): NoteGenerateErrorName => {
  if (status === 401) return 'NoteGenerateInterrupted';
  if (status === 403) return 'NoteGeneratePermissionDenied';
  if (status === 408 || status === 504) return 'NoteGenerateTimeout';
  if (status === 429) return 'NoteGenerateRateLimited';
  if (status >= 500) return 'NoteGenerateServerError';
  if (status >= 400) return 'NoteGenerateRejected';
  return 'NoteGenerateUnclassified';
};

/**
 * Maps a web failure code (`parseNoteFailedErrorCode`) to a bounded name. A
 * fresh named error is returned so a raw error's name never reaches the classifier.
 */
export const classifyNoteGenerateFailureCode = (code: string): Error => {
  const known = NAME_BY_CODE[code];
  if (known) return namedError(known);
  const http = /^HTTP_(\d{3})$/.exec(code);
  return namedError(
    http ? nameForHttpStatus(Number(http[1])) : 'NoteGenerateUnclassified'
  );
};

/**
 * Maps a Desktop engine coarse `error_code` (`uploader_coarse_error_code`). The
 * engine sends only `http_<status>`, so a quota or deleted-template rejection
 * lands as `request_rejected` / `permission_denied`, not the web's finer types.
 */
export const classifyDesktopNoteGenerateErrorCode = (
  code: string | null | undefined
): Error => {
  if (code === 'unauthorized') return namedError('NoteGenerateInterrupted');
  if (code === 'timeout') return namedError('NoteGenerateTimeout');
  if (code === 'network') return namedError('NoteGenerateNetworkUnavailable');
  if (code === 'transcription_failed') {
    return namedError('NoteGenerateTranscriptionFailed');
  }
  const http = code ? /^http_(\d{3})$/.exec(code) : null;
  return namedError(
    http ? nameForHttpStatus(Number(http[1])) : 'NoteGenerateUnclassified'
  );
};

// Browser messages for a fetch that fails or whose body errors mid-read
// (Firefox: "Error in input stream"); any other TypeError is a client bug.
const FETCH_FAILURE_MESSAGE =
  /failed to fetch|load failed|network ?error|network connection was lost|internet connection appears to be offline|error in input stream/i;

export const isFetchFailure = (error: unknown): boolean =>
  error instanceof TypeError && FETCH_FAILURE_MESSAGE.test(error.message);

export const createAllCandidatesFailedError = (): Error =>
  namedError('NoteGenerateAllCandidatesFailed');

export const createNoteGenerateCancelledError = (): Error =>
  namedError('NoteGenerateCancelled');

/**
 * Stream recovery found the server note failed (classified by its failure
 * reason), never started, or gave up waiting for it.
 */
export const createRecoveredStreamFailureError = (
  outcome: 'failed' | 'timed_out' | 'never_started',
  failureReason?: string
): Error => {
  if (outcome === 'timed_out') return namedError('NoteGenerateTimeout');
  const known = failureReason ? NAME_BY_CODE[failureReason] : undefined;
  return namedError(outcome === 'failed' && known ? known : 'NoteGenerateServerError');
};

/** Tags an auth-ended generation so it stays queryable inside `cancelled`. */
export const interruptionMetadata = (error: Error): { cancel_reason?: 'unauthorized' } =>
  error.name === 'NoteGenerateInterrupted' ? { cancel_reason: 'unauthorized' } : {};

// Telemetry must never change the generation's outcome.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logger.warn('[noteGenerateTracking] telemetry step failed', {
      step,
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
};

/** Emits intent for an accepted explicit generate; a no-op for autonomous work. */
export const recordNoteGenerateIntent = (trigger: NoteGenerateTrigger): void => {
  if (trigger !== 'explicit') return;
  safely('intent', () => recordSessionFlowIntent(TrackingKey.SCRIBE_NOTE_GENERATION));
};

// ── Desktop engine (Rust `session_engine:consult_note`) ─────────────────────
// Autonomous work, so attempt only. One open attempt per session.

export type DesktopNoteGenerateSource = NoteGenerationAnalyticsSource;

// Held for the app's lifetime on purpose: a Rust abort sends no terminal, and
// the next `started` for that session closes the entry as superseded.
const desktopAttempts = new Map<string, TrackingHandle>();

const closeDesktopAttempt = (
  sessionId: string,
  close: (handle: TrackingHandle) => void
): boolean => {
  const handle = desktopAttempts.get(sessionId);
  if (!handle) return false;
  desktopAttempts.delete(sessionId);
  close(handle);
  return true;
};

const startDesktopAttempt = (
  sessionId: string,
  source: DesktopNoteGenerateSource,
  startObserved: boolean
): TrackingHandle =>
  startTracking({
    key: TrackingKey.SCRIBE_NOTE_GENERATION,
    instanced: true,
    metadata: {
      session_id: sessionId,
      generation_path: 'desktop_engine',
      // Rust owns the work, so a webview reload must not report it abandoned.
      outlives_page: true,
      source,
      trigger: 'autonomous',
      ...(startObserved ? {} : { start_unobserved: true }),
    },
  });

export const beginDesktopNoteGenerateAttempt = (
  sessionId: string,
  source: DesktopNoteGenerateSource
): void =>
  safely('desktop_begin', () => {
    // A second `started` for the same session supersedes the first.
    closeDesktopAttempt(sessionId, (handle) =>
      finishTracking({
        handle,
        outcome: 'failed',
        error: createNoteGenerateCancelledError(),
        metadata: { cancel_reason: 'superseded' },
      })
    );
    desktopAttempts.set(sessionId, startDesktopAttempt(sessionId, source, true));
  });

/**
 * Closes the session's attempt. A terminal whose `started` this webview never
 * saw (it mounted mid-generation) still counts, with a near-zero duration.
 */
const finishDesktopAttempt = (
  step: string,
  sessionId: string,
  source: DesktopNoteGenerateSource,
  finish: (handle: TrackingHandle) => void
): void =>
  safely(step, () => {
    if (closeDesktopAttempt(sessionId, finish)) return;
    finish(startDesktopAttempt(sessionId, source, false));
  });

export const completeDesktopNoteGenerateAttempt = (
  sessionId: string,
  source: DesktopNoteGenerateSource
): void =>
  finishDesktopAttempt('desktop_complete', sessionId, source, (handle) =>
    finishTracking({ handle, metadata: { result: 'success' } })
  );

export const failDesktopNoteGenerateAttempt = (
  sessionId: string,
  source: DesktopNoteGenerateSource,
  errorCode: string | null | undefined
): void =>
  finishDesktopAttempt('desktop_fail', sessionId, source, (handle) => {
    const error = classifyDesktopNoteGenerateErrorCode(errorCode);
    finishTracking({
      handle,
      outcome: 'failed',
      error,
      metadata: { result: 'error', ...interruptionMetadata(error) },
    });
  });

/** The user deleted the offline session mid-generation. */
export const cancelDesktopNoteGenerateAttempt = (sessionId: string): void =>
  safely('desktop_cancel', () => {
    closeDesktopAttempt(sessionId, (handle) =>
      finishTracking({
        handle,
        outcome: 'failed',
        error: createNoteGenerateCancelledError(),
        metadata: { cancel_reason: 'deleted' },
      })
    );
  });

export const __resetDesktopNoteGenerateAttemptsForTests = (): void =>
  desktopAttempts.clear();
